import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { useActiveTenant } from '../contexts/tenant'
import type { Product } from '@wsfull/shared'

type Adjustment = {
  id: string; movedAt: string; productId: string; locationId: string
  quantity: number; notes: string | null; createdBy: string | null
  sku: string | null; productName: string | null; locationCode: string | null
}
type StockBalance = {
  productId: string; locationId: string; quantity: number
  sku: string | null; productName: string | null; locationCode: string | null
}
type Location = { id: string; code: string; active: boolean }

const typeOptions = [
  { value: 'in',         label: '(+) Entrada',   desc: 'Adiciona ao saldo existente' },
  { value: 'out',        label: '(−) Saída',      desc: 'Remove do saldo existente' },
  { value: 'correction', label: '(=) Correção',   desc: 'Define a quantidade exata (inventário)' },
]

export function AdjustmentsPage() {
  const tenantId = useActiveTenant()
  const qc = useQueryClient()
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState({ productId: '', locationId: '', type: 'in', quantity: '', reason: '', lotId: '' })
  const [preview, setPreview] = useState<{ current: number; after: number } | null>(null)
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 25

  const { data: adjustments = [], isLoading } = useQuery<Adjustment[]>({
    queryKey: ['adjustments', tenantId],
    queryFn: async () => {
      if (!tenantId) return []
      return (await api.get('/stock/adjustments', { params: { tenantId, limit: 500 } })).data
    },
    enabled: !!tenantId,
  })

  const total = adjustments.length
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const safePage = Math.min(page, pageCount)
  const paged = adjustments.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['products', tenantId],
    queryFn: async () => (await api.get('/products', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  const { data: balances = [] } = useQuery<StockBalance[]>({
    queryKey: ['stock', 'balance', tenantId],
    queryFn: async () => (await api.get('/stock/balance', { params: { tenantId } })).data,
    enabled: !!tenantId && modal,
  })

  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ['stock', 'locations'],
    queryFn: async () => (await api.get('/stock/locations')).data,
    enabled: modal,
  })

  // Todas as localizações ativas, com o saldo do produto ao lado — sem isso não dá
  // para dar entrada num local zerado. GERAL (fracionamento) sempre em primeiro.
  const locationsForProduct = locations
    .filter(l => l.active)
    .map(l => ({
      locationId: l.id,
      locationCode: l.code === 'GERAL' ? 'GERAL — Fracionamento' : l.code,
      quantity: balances.find(b => b.productId === form.productId && b.locationId === l.id)?.quantity ?? 0,
    }))
    .sort((a, b) => (b.locationCode.startsWith('GERAL') ? 1 : 0) - (a.locationCode.startsWith('GERAL') ? 1 : 0)
      || a.locationCode.localeCompare(b.locationCode))

  const currentBalance = balances.find(b => b.productId === form.productId && b.locationId === form.locationId)?.quantity ?? 0

  function calcPreview() {
    const qty = parseInt(form.quantity) || 0
    if (!form.locationId || !qty) { setPreview(null); return }
    const after = form.type === 'correction' ? qty
      : form.type === 'in' ? currentBalance + qty
      : currentBalance - qty
    setPreview({ current: currentBalance, after })
  }

  const create = useMutation({
    mutationFn: async () => api.post('/stock/adjust', {
      tenantId,
      productId: form.productId,
      locationId: form.locationId,
      type: form.type,
      quantity: parseInt(form.quantity),
      reason: form.reason,
      lotId: form.lotId || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adjustments', tenantId] })
      qc.invalidateQueries({ queryKey: ['stock', 'balance', tenantId] })
      setModal(false)
      setForm({ productId: '', locationId: '', type: 'in', quantity: '', reason: '', lotId: '' })
      setPreview(null)
    },
  })

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  const canCreate = form.productId && form.locationId && form.quantity && form.reason

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Ajustes de Estoque</h2>
          <p className="text-sm text-gray-500">Correções manuais com justificativa — trilha de auditoria completa</p>
        </div>
        <Button size="sm" onClick={() => setModal(true)}><Plus className="h-4 w-4" /> Novo ajuste</Button>
      </div>

      <Modal title="Novo ajuste de estoque" open={modal} onClose={() => setModal(false)} size="md">
        <div className="space-y-4">
          {/* Tipo de ajuste */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Tipo de ajuste *</label>
            <div className="grid grid-cols-3 gap-2">
              {typeOptions.map(opt => (
                <button key={opt.value}
                  onClick={() => { setForm(p => ({ ...p, type: opt.value })); setPreview(null) }}
                  className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                    form.type === opt.value
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}>
                  <p className="font-semibold text-sm">{opt.label}</p>
                  <p className="text-xs opacity-75 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Produto + Localização */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Produto *</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={form.productId}
                onChange={e => { setForm(p => ({ ...p, productId: e.target.value, locationId: '' })); setPreview(null) }}>
                <option value="">Selecione...</option>
                {products.filter(p => p.active).map(p => (
                  <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Localização *</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={form.locationId}
                onChange={e => { setForm(p => ({ ...p, locationId: e.target.value })); setPreview(null) }}>
                <option value="">Selecione...</option>
                {locationsForProduct.map(b => (
                  <option key={b.locationId} value={b.locationId}>
                    {b.locationCode} — saldo: {b.quantity}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                Só fracionamento. Caixa fechada em palete se corrige em Paletes ou Contagem.
              </p>
            </div>
          </div>

          {/* Quantidade + preview */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Quantidade * {form.type === 'correction' ? '(nova quantidade exata)' : ''}
            </label>
            <input type="number" min={0} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={form.quantity}
              onChange={e => { setForm(p => ({ ...p, quantity: e.target.value })); setPreview(null) }}
              onBlur={calcPreview}
              placeholder={form.type === 'correction' ? 'Quantidade final' : 'Quantidade a ajustar'}
            />
          </div>

          {preview && (
            <div className="flex items-center gap-3 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm">
              <span className="text-gray-500">Saldo atual: <strong className="text-gray-900">{preview.current}</strong></span>
              <span className="text-gray-400">→</span>
              <span className={`font-semibold ${preview.after < 0 ? 'text-red-600' : preview.after > preview.current ? 'text-green-600' : 'text-blue-600'}`}>
                {preview.after} unidades
              </span>
              {preview.after < 0 && <span className="text-red-500 text-xs">⚠ Saldo negativo</span>}
            </div>
          )}

          {/* Justificativa */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Justificativa * <span className="font-normal text-gray-400">(obrigatória para auditoria)</span></label>
            <textarea rows={2} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none"
              placeholder="Ex: Inventário físico realizado em 01/06/2026 — divergência encontrada"
              value={form.reason}
              onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} />
          </div>

          {create.error && (
            <p className="text-sm text-red-600">{(create.error as any)?.response?.data?.detail ?? (create.error as any)?.response?.data?.title ?? 'Erro ao ajustar'}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setModal(false)}>Cancelar</Button>
            <Button loading={create.isPending} disabled={!canCreate} onClick={() => create.mutate()}>
              Confirmar ajuste
            </Button>
          </div>
        </div>
      </Modal>

      {/* Histórico */}
      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">Histórico de ajustes</span>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : adjustments.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhum ajuste registrado</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Data</th>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Produto</th>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Local</th>
                  <th className="px-5 py-3 text-right font-medium text-gray-600">Quantidade</th>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Justificativa</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paged.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{new Date(a.movedAt).toLocaleString('pt-BR')}</td>
                    <td className="px-5 py-3 text-gray-700">{a.productName ?? a.sku ?? '—'}</td>
                    <td className="px-5 py-3 font-mono text-gray-700">{a.locationCode ?? '—'}</td>
                    <td className={`px-5 py-3 text-right font-semibold ${a.quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {a.quantity > 0 ? '+' : ''}{a.quantity}
                    </td>
                    <td className="px-5 py-3 text-gray-600 max-w-xs truncate">{a.notes ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3 text-sm">
              <span className="text-gray-500">
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, total)} de {total}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>Anterior</Button>
                <span className="text-gray-500">{safePage}/{pageCount}</span>
                <Button variant="secondary" size="sm" disabled={safePage >= pageCount} onClick={() => setPage(safePage + 1)}>Próxima</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
